<template>
    <MContainerColumn class="-mt-2 w-full gap-4" fluid>
        <MContainerRow class="gap-4">
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="value">{{ $t("shared.firstName") }}</label>
                <InputText id="editPatientDialogName" class="w-full" v-model="model.extra.firstName" type="text" />
            </MContainerColumn>
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="value">{{ $t("shared.lastName") }}</label>
                <InputText id="editPatientDialogLastName" class="w-full" v-model="model.extra.lastName" type="text" />
            </MContainerColumn>
        </MContainerRow>
        <MContainerRow class="gap-4">
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="patientid">{{ $t("shared.patientId") }}</label>
                <InputText id="patientid" class="w-full" v-model="model.extra.patientID" disabled />
            </MContainerColumn>
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="studyinstance">{{ $t("web-rg.examId") }}</label>
                <InputText id="studyinstance" class="w-full" v-model="model.extra.study_instance_UID" disabled />
            </MContainerColumn>
        </MContainerRow>
        <MContainerRow class="gap-4">
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="note">{{ $t("shared.note") }}</label>
                <InputText id="note" class="w-full" v-model="model.note" />
            </MContainerColumn>
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="modality">{{ $t("web-rg.modality") }}</label>
                <InputText id="modality" class="w-full" v-model="model.extra.modality" />
            </MContainerColumn>
        </MContainerRow>
        <MContainerRow class="gap-4">
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="doctor">{{ $t("web-rg.doctor") }}</label>
                <InputText id="doctor" class="w-full" v-model="doctorName" :placeholder="$t('web-rg.unassignedDoctor')" readonly disabled />
            </MContainerColumn>
            <MContainerColumn class="basis-full md:basis-0 grow min-w-0 gap-2">
                <label for="dateOfBirth">{{ $t("web-rg.dateOfBirth") }}</label>
                <DatePicker v-model="dateOfBirth" class="w-full" showIcon />
            </MContainerColumn>
        </MContainerRow>
        <Divider />
    </MContainerColumn>

    <MContainer>
        <MContainer v-if="iuState === UIState.INIT" class="w-full justify-center gap-2 items-center">
            <MDialogFooter class="mt-4" :acceptLabel="$t('shared.save')" :rejectLabel="$t('shared.cancel')" @accept="savePatient" @reject="dialogRef.close()" />
        </MContainer>
        <MContainer v-else class="w-full justify-center">
            <ProgressSpinner style="width: 50px; height: 50px" animationDuration=".75s" />
        </MContainer>
    </MContainer>
</template>
<script lang="ts" setup>
import type { DynamicDialogOptions } from "primevue/dynamicdialogoptions";
import { PatientListModel } from "@/models/PatientListModel";
import { usePatientsStore } from "@/stores/PatientsStore";
import { useToast } from "primevue/usetoast";
import { DoctorModel } from "@xlabmedical/medic-api";
import DatePicker from "primevue/datepicker";
import { MContainer, MDialogFooter, MContainerColumn, MContainerRow } from "@xlabmedical/medic-web-framework";

enum UIState {
    INIT,
    SAVING,
}

const iuState = ref<UIState>(UIState.INIT);

const patientStore = usePatientsStore();
const toast = useToast();

const dialogRef = inject("dialogRef") as DynamicDialogOptions;
const params = dialogRef.value.data as { patientData: PatientListModel; doctors: DoctorModel[] };

const emits = defineEmits<{ (e: "patientUpdated", value: string): void }>();

let doB = undefined;
if (params.patientData.dateOfBirth) {
    doB = params.patientData.dateOfBirth.getFullYear() > 1800 ? params.patientData.dateOfBirth : undefined;
}
const model: PatientListModel = {
    patientName: params.patientData.patientName,
    note: params.patientData.note ?? "",
    dateOfBirth: doB,
    extra: {
        patientName: params.patientData.firstName,
        firstName: params.patientData.firstName,
        lastName: params.patientData.lastName,
        patientID: params.patientData.patientName,
        modality: params.patientData.extra ? params.patientData.extra.modality : "",
        doctorID: params.patientData.extra ? params.patientData.extra.doctorID : "",
        study_instance_UID: params.patientData.extra ? params.patientData.extra.study_instance_UID : "",
    },
    shares: params.patientData.shares,
    hasWeblinkOrder: params.patientData.hasWeblinkOrder,
};

const dateOfBirth = ref(model.dateOfBirth);

const resolveDoctorName = () => {
    const patientDataRecord = params.patientData as unknown as Record<string, unknown>;
    const patientExtraRecord = (params.patientData.extra ?? {}) as Record<string, unknown>;

    const rawDoctorName = [
        patientDataRecord.doctorName,
        patientDataRecord.doctor_name,
        patientDataRecord.doctorFullName,
        patientDataRecord.doctor_full_name,
        patientExtraRecord.doctorName,
        patientExtraRecord.doctor_name,
        patientExtraRecord.doctorFullName,
        patientExtraRecord.doctor_full_name,
        patientExtraRecord.doctor,
    ].find((value) => typeof value === "string" && value.trim().length > 0);

    if (typeof rawDoctorName === "string") {
        return rawDoctorName;
    }

    const rawDoctorData = patientDataRecord.doctor ?? patientExtraRecord.doctor;
    if (rawDoctorData && typeof rawDoctorData === "object") {
        const doctorRecord = rawDoctorData as Record<string, unknown>;
        const objectDoctorName = [
            doctorRecord.fullName,
            doctorRecord.name,
            doctorRecord.doctorName,
            doctorRecord.doctor_name,
            [doctorRecord.firstName, doctorRecord.lastName].filter((value) => typeof value === "string" && value.trim().length > 0).join(" "),
            [doctorRecord.first_name, doctorRecord.last_name].filter((value) => typeof value === "string" && value.trim().length > 0).join(" "),
        ].find((value) => typeof value === "string" && value.trim().length > 0);

        if (typeof objectDoctorName === "string") {
            return objectDoctorName;
        }
    }

    const doctorId = params.patientData.extra ? Number(params.patientData.extra.doctorID) : NaN;
    const mappedDoctor = params.doctors.find((doctor) => doctor.id === doctorId);
    if (mappedDoctor) {
        return `${mappedDoctor.firstName} ${mappedDoctor.lastName}`.trim();
    }

    return "";
};

const doctorName = ref(resolveDoctorName());

const savePatient = async () => {
    iuState.value = UIState.SAVING;

    const requestData = {
        notes: model.note,
        dateOfBirth: dateOfBirth.value,
        firstName: model.extra.firstName,
        lastName: model.extra.lastName,
        extra: {
            patientName: model.extra.firstName,
            lastName: model.extra.lastName,
            patientID: model.extra.patientID,
            modality: model.extra.modality,
            doctorID: params.patientData.extra ? params.patientData.extra.doctorID : "",
            study_instance_UID: params.patientData.extra ? params.patientData.extra.study_instance_UID : "",
        },
    };

    const projectName = model.extra.patientID ?? model.extra.study_instance_UID ?? "project name not found";

    await patientStore.updatePatientData(projectName, JSON.stringify(requestData));
    toast.add({ severity: "success", summary: "Patient updated", detail: "You have updated the patient ", life: 3000 });

    emits("patientUpdated", model.extra.study_instance_UID ?? "");

    dialogRef.value.close();
};
</script>
<style scoped>
:deep(.p-inputtext) {
    background: rgba(255, 255, 255, 0.05);
    padding: 0.429rem 0.571rem;
    border-radius: 0.5rem;
    border: 1px var(--p-rg-border-color) solid !important;
}

:deep(.p-datepicker-dropdown) {
    background: var(--p-primary-color);
    color: white;
    border: 1px solid var(--p-primary-color) !important;
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
}

:deep(.p-datepicker .p-datepicker-input) {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
}
</style>
